import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { useSelector, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";

// Icons - reused from ScheduleSurvey
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const AddRecipientsModal = ({ isOpen, onClose, existingRecipients = [], onAddRecipients }) => {
  const { addToast } = useToasts();
  
  // Selection state - same as ScheduleSurvey
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("individual"); // "individual" or "category"
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Redux state - same as ScheduleSurvey
  const {
    employees,
    get_employee_org_category,
    get_survey_structure,
  } = useSelector(
    (state) => ({
      employees: Array.isArray(state.employees?.employees) 
        ? state.employees.employees 
        : [],
      get_employee_org_category: Array.isArray(state.employee_category?.employee_org_category) 
        ? state.employee_category.employee_org_category 
        : [],
      get_survey_structure: state.surveystructure?.survey_structure || [],
    }),
    shallowEqual
  );

  // Build category filter options from survey structure - same as ScheduleSurvey
  const filterCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    const categories = get_survey_structure[0].categories
      .filter(cat => cat.visibility !== "private" && !cat.demographic);

    const primary = categories.find(c => c.priority === "primary");
    const result = [];

    // Non-primary categories as-is
    categories.forEach(cat => {
      if (cat.priority !== "primary") {
        result.push({
          id: cat.id,
          name: cat.name,
          priority: cat.priority,
          options: (cat.options || []).map(opt => ({
            id: opt.id,
            name: opt.name,
            text: opt.text || opt.name,
          }))
        });
      }
    });

    // Primary: split into base primary options without subfactors and parent-option groups for subfactors
    if (primary) {
      const basePrimaryOptions = (primary.options || []).filter(opt => !Array.isArray(opt.subfactors) || opt.subfactors.length === 0);
      if (basePrimaryOptions.length > 0) {
        result.push({
          id: primary.id,
          name: primary.name,
          priority: primary.priority,
          options: basePrimaryOptions.map(opt => ({
            id: opt.id,
            name: opt.name,
            text: opt.text || opt.name,
          })),
        });
      }

      (primary.options || [])
        .filter(opt => Array.isArray(opt.subfactors) && opt.subfactors.length > 0)
        .forEach(parent => {
          result.push({
            id: `primary-parent-${parent.id}`,
            name: parent.name,
            priority: "primary",
            options: parent.subfactors.map(sf => ({
              id: sf.id,
              name: sf.name,
              text: sf.text || sf.name,
            })),
          });
        });
    }

    return result;
  }, [get_survey_structure]);

  // Filter employees excluding existing recipients - same logic as ScheduleSurvey but with exclusion
  const availableEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    
    return employees.filter(emp => 
      emp.status === 1 && // Only active employees
      !existingRecipients.includes(emp.id) // Exclude existing recipients
    );
  }, [employees, existingRecipients]);

  // Filter employees based on search term and selected categories - same as ScheduleSurvey
  const filteredEmployees = useMemo(() => {
    let filtered = availableEmployees;
    
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(emp => {
        const empCategories = get_employee_org_category.find(
          empCat => (empCat?.employee === emp.id || empCat?.employee_id === emp.id)
        );
        
        if (!Array.isArray(empCategories?.category)) return false;
        
        return selectedCategories.some(selectedCatId => {
          const selNum = Number(selectedCatId);
          return empCategories.category.some(cat => {
            if (!cat) return false;
            const valIdNum = Number(cat.value_id);
            return (
              valIdNum === selNum ||
              cat.value_id === selectedCatId ||
              cat.value === selectedCatId ||
              Number(cat.value) === selNum
            );
          });
        });
      });
    } else if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.first_name?.toLowerCase().includes(lowerSearch) ||
        emp.last_name?.toLowerCase().includes(lowerSearch) ||
        emp.email?.toLowerCase().includes(lowerSearch)
      );
    }
    
    return filtered;
  }, [availableEmployees, searchTerm, selectedCategories, get_employee_org_category]);

  const addableEmployees = useMemo(
    () => filteredEmployees.filter(emp => !selectedEmployees.includes(emp.id)),
    [filteredEmployees, selectedEmployees]
  );

  // Handle employee selection - same as ScheduleSurvey
  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Handle category selection - same as ScheduleSurvey
  const handleCategoryToggle = (categoryOptionId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryOptionId)
        ? prev.filter(id => id !== categoryOptionId)
        : [...prev, categoryOptionId]
    );
  };

  // Toggle category expand - same as ScheduleSurvey
  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategory(prev => prev === categoryId ? null : categoryId);
  };

  // Add employees from category selection - same as ScheduleSurvey
  const addCategoryEmployees = () => {
    const employeeIds = addableEmployees.map(emp => emp.id);

    if (employeeIds.length === 0) {
      setSelectedCategories([]);
      setExpandedCategory(null);
      return;
    }

    setSelectedEmployees(prev => [...new Set([...prev, ...employeeIds])]);
    setSelectedCategories([]);
    setExpandedCategory(null);
  };

  // Clear all selected employees - same as ScheduleSurvey
  const clearAllEmployees = () => {
    setSelectedEmployees([]);
    setSelectedCategories([]);
    setExpandedCategory(null);
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedEmployees([]);
    setSearchTerm("");
    setSelectedCategories([]);
    setExpandedCategory(null);
    setActiveTab("individual");
    onClose();
  };

  // Handle adding recipients
  const handleAddRecipients = () => {
    if (selectedEmployees.length === 0) {
      addToast("Please select at least one recipient", { appearance: "error" });
      return;
    }
    
    onAddRecipients(selectedEmployees);
    addToast(`Queued ${selectedEmployees.length} recipient${selectedEmployees.length !== 1 ? 's' : ''} to add`, {
      appearance: "success"
    });
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer data-testid="add-recipients-modal" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add Recipients to Survey</ModalTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {/* Tab Navigation - same as ScheduleSurvey */}
          <TabContainer>
            <Tab 
              type="button"
              active={activeTab === "individual"} 
              onClick={() => {
                setActiveTab("individual");
                setSearchTerm("");
                setSelectedCategories([]);
              }}
            >
              Individual Selection
            </Tab>
            <Tab 
              type="button"
              active={activeTab === "category"} 
              onClick={() => {
                setActiveTab("category");
                setSearchTerm("");
              }}
            >
              By Category
            </Tab>
          </TabContainer>

          <EmployeeSelectionLayout>
            {/* Left: Selection Interface - same as ScheduleSurvey */}
            <SelectionPanel>
              {/* Individual Selection Tab */}
              {activeTab === "individual" && (
                <SelectionContent>
                  <SearchContainer>
                    <SearchInputWrapper>
                      <SearchIconWrapper>
                        <SearchIcon />
                      </SearchIconWrapper>
                      <SearchInput
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search available contacts..."
                      />
                    </SearchInputWrapper>
                    <SearchHint>
                      {searchTerm ? (
                        `${filteredEmployees.length} contact${filteredEmployees.length !== 1 ? 's' : ''} found`
                      ) : (
                        `${availableEmployees.length} available contacts`
                      )}
                    </SearchHint>
                  </SearchContainer>

                  {searchTerm ? (
                    <EmployeesList>
                      {filteredEmployees.map(emp => (
                        <EmployeeItem 
                          key={emp.id}
                          selected={selectedEmployees.includes(emp.id)}
                          onClick={() => handleEmployeeToggle(emp.id)}
                        >
                          <EmployeeCheckbox
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.id)}
                            onChange={(e) => { e.stopPropagation(); handleEmployeeToggle(emp.id); }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <EmployeeDetails>
                            <EmployeeName>{emp.first_name} {emp.last_name}</EmployeeName>
                            <EmployeeEmail>{emp.email}</EmployeeEmail>
                          </EmployeeDetails>
                        </EmployeeItem>
                      ))}
                      {filteredEmployees.length === 0 && (
                        <EmptyState>
                          <EmptyTitle>No available contacts found</EmptyTitle>
                          <EmptyText>Try adjusting your search terms</EmptyText>
                        </EmptyState>
                      )}
                    </EmployeesList>
                  ) : (
                    <SearchPrompt>
                      <SearchPromptIcon>👥</SearchPromptIcon>
                      <SearchPromptTitle>Search for contacts</SearchPromptTitle>
                      <SearchPromptText>
                        Start typing to find available contacts by name or email
                      </SearchPromptText>
                    </SearchPrompt>
                  )}
                </SelectionContent>
              )}

              {/* Category Selection Tab - same as ScheduleSurvey */}
              {activeTab === "category" && (
                <SelectionContent>
                  {selectedCategories.length > 0 && (
                    <CategoryAddSection>
                      <CategoryAddInfo>
                        {addableEmployees.length > 0
                          ? `${addableEmployees.length} new contact${
                              addableEmployees.length !== 1 ? 's' : ''
                            } ready to add${
                              filteredEmployees.length > addableEmployees.length
                                ? ` (${filteredEmployees.length - addableEmployees.length} already selected)`
                                : ''
                            }`
                          : filteredEmployees.length > 0
                          ? 'Everyone matching this selection is already selected'
                          : 'No contacts match this selection'}
                      </CategoryAddInfo>
                      <CategoryAddButton
                        onClick={addCategoryEmployees}
                        disabled={addableEmployees.length === 0}
                      >
                        Add {addableEmployees.length} Contact
                        {addableEmployees.length !== 1 ? 's' : ''}
                      </CategoryAddButton>
                    </CategoryAddSection>
                  )}

                  {filterCategories.length > 0 ? (
                    <CategoryFilters>
                      {filterCategories.map(category => (
                        <CategoryGroup key={category.id}>
                          <CategoryGroupHeader onClick={() => toggleCategoryExpand(category.id)}>
                            <CategoryGroupLeft>
                              <CategoryCollapseIcon expanded={expandedCategory === category.id}>
                                {expandedCategory === category.id ? '▼' : '▶'}
                              </CategoryCollapseIcon>
                              <CategoryName>{category.name}</CategoryName>
                            </CategoryGroupLeft>
                            <CategoryCount>
                              {category.options.filter(opt => selectedCategories.includes(opt.id)).length} of {category.options.length}
                            </CategoryCount>
                          </CategoryGroupHeader>
                          
                          {expandedCategory === category.id && (
                            <CategoryOptions>
                              {category.options.map(option => (
                                <CategoryOption
                                  key={option.id}
                                  selected={selectedCategories.includes(option.id)}
                                  onClick={() => handleCategoryToggle(option.id)}
                                >
                                  <CategoryOptionCheckbox
                                    type="checkbox"
                                    checked={selectedCategories.includes(option.id)}
                                    readOnly
                                  />
                                  <CategoryOptionText>{option.name}</CategoryOptionText>
                                </CategoryOption>
                              ))}
                            </CategoryOptions>
                          )}
                        </CategoryGroup>
                      ))}
                    </CategoryFilters>
                  ) : (
                    <EmptyState>
                      <EmptyTitle>No categories available</EmptyTitle>
                      <EmptyText>Contact your administrator to set up contact categories</EmptyText>
                    </EmptyState>
                  )}
                </SelectionContent>
              )}
            </SelectionPanel>

            {/* Right: Selected Employees - same as ScheduleSurvey */}
            <SelectedPanel>
              <SelectedPanelHeader>
                <SummaryTitle>Selected to Add ({selectedEmployees.length})</SummaryTitle>
                {selectedEmployees.length > 0 && (
                  <ClearButton onClick={clearAllEmployees}>Clear All</ClearButton>
                )}
              </SelectedPanelHeader>
              
              {selectedEmployees.length > 0 ? (
                <SelectedEmployeesFullList>
                  {selectedEmployees.map(empId => {
                    const emp = availableEmployees.find(e => e.id === empId);
                    return emp ? (
                      <SelectedEmployeeFullCard key={empId}>
                        <SelectedEmployeeDetails>
                          <SelectedEmployeeName>{emp.first_name} {emp.last_name}</SelectedEmployeeName>
                          <SelectedEmployeeEmail>{emp.email}</SelectedEmployeeEmail>
                          {emp.role_name && <SelectedEmployeeRole>{emp.role_name}</SelectedEmployeeRole>}
                        </SelectedEmployeeDetails>
                        <RemoveButton onClick={() => handleEmployeeToggle(empId)}>×</RemoveButton>
                      </SelectedEmployeeFullCard>
                    ) : null;
                  })}
                </SelectedEmployeesFullList>
              ) : (
                <EmptySelectedState>
                  <EmptySelectedIcon>👥</EmptySelectedIcon>
                  <EmptySelectedTitle>No recipients selected</EmptySelectedTitle>
                  <EmptySelectedText>
                    Use the selection options on the left to choose recipients to add.
                  </EmptySelectedText>
                </EmptySelectedState>
              )}
            </SelectedPanel>
          </EmployeeSelectionLayout>
        </ModalContent>

        <ModalFooter>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <AddButton 
            data-testid="add-recipients-confirm"
            onClick={handleAddRecipients}
            disabled={selectedEmployees.length === 0}
          >
            Add {selectedEmployees.length} Recipient{selectedEmployees.length !== 1 ? 's' : ''}
          </AddButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default AddRecipientsModal;

// Styled Components - reusing from ScheduleSurvey with modal-specific styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const ModalTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 24px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.disabled ? '#e0e0e0' : '#476dfa'};
  border: none;
  border-radius: 7px;
  color: ${props => props.disabled ? '#aaa' : 'white'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;

// Reused styled components from ScheduleSurvey
const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? '#476dfa' : 'transparent'};
  border: none;
  border-radius: 6px;
  color: ${props => props.active ? 'white' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? '#3d5ce6' : '#e9ecef'};
  }
`;

const EmployeeSelectionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 24px;
  flex: 1;
  min-height: 0;
`;

const SelectionPanel = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SelectionContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SearchContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 8px;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const SearchHint = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #7e7e7e;
  padding-left: 12px;
`;

const EmployeesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const EmployeeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid ${props => props.selected ? '#476dfa' : 'transparent'};

  &:hover {
    background: ${props => props.selected ? '#f0f4ff' : '#f8f9fa'};
  }
`;

const EmployeeCheckbox = styled.input`
  margin: 0;
  cursor: pointer;
`;

const EmployeeDetails = styled.div`
  flex: 1;
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const SearchPrompt = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const SearchPromptIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const SearchPromptTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const SearchPromptText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const CategoryAddSection = styled.div`
  padding: 12px 16px;
  background: #f0f4ff;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CategoryAddInfo = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #476dfa;
  font-weight: 500;
`;

const CategoryAddButton = styled.button`
  padding: 6px 12px;
  background: #476dfa;
  border: none;
  border-radius: 4px;
  color: white;
  font-family: "Poppins";
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }

  &:disabled {
    background: #a7b8ff;
    cursor: not-allowed;
    opacity: 0.85;
  }

  &:disabled:hover {
    background: #a7b8ff;
  }
`;

const CategoryFilters = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const CategoryGroup = styled.div`
  border-bottom: 1px solid #f0f0f0;
`;

const CategoryGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const CategoryGroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCollapseIcon = styled.span`
  font-size: 12px;
  color: #7e7e7e;
  transition: transform 0.2s;
  transform: ${props => props.expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
`;

const CategoryName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
`;

const CategoryCount = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #7e7e7e;
`;

const CategoryOptions = styled.div`
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
`;

const CategoryOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 8px 40px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-left: 2px solid ${props => props.selected ? '#476dfa' : 'transparent'};

  &:hover {
    background: #f0f0f0;
  }
`;

const CategoryOptionCheckbox = styled.input`
  margin: 0;
  cursor: pointer;
`;

const CategoryOptionText = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #414141;
`;

const SelectedPanel = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const SelectedPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const SummaryTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  font-family: "Poppins";
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: #fff5f5;
  }
`;

const SelectedEmployeesFullList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const SelectedEmployeeFullCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 6px;
  background: white;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1);
  }
`;

const SelectedEmployeeDetails = styled.div`
  flex: 1;
`;

const SelectedEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
`;

const SelectedEmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const SelectedEmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #7e7e7e;
  background: #f0f4ff;
  padding: 1px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 2px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: #fff5f5;
  }
`;

const EmptySelectedState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptySelectedIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptySelectedTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const EmptySelectedText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin: 0;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin: 0;
`;
